import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Link, useLocation, useMatchRoute } from '@ui/app-router';
import { SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem } from '@ui/primitives/sidebar';
import { Skeleton } from '@ui/primitives/skeleton';
import { cn } from '@ui/primitives/utils';
import { ChevronRight, Folder, Package } from 'lucide-react';
import { useState } from 'react';
import { useGetProjectApplicationSidebarList } from 'ze-sdk';
import { NavLink } from '../../../components/nav-link';
import { ApplicationContextMenu } from './application-context-menu';
import { ProjectContextMenu } from './project-context-menu';
function AppSubItem(param) {
    let { app, organization, project, searchQuery } = param;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    return /*#__PURE__*/ _jsxs(SidebarMenuSubItem, {
        className: cn('group/app-item', isMenuOpen && 'bg-sidebar-accent rounded-md'),
        "data-e2e": "LAYOUT_SIDEBAR__APPLICATION_ITEM",
        "data-application-name": app.name,
        children: [
            /*#__PURE__*/ _jsx(SidebarMenuSubButton, {
                asChild: true,
                children: /*#__PURE__*/ _jsx(NavLink, {
                    to: "/org/$organization/$project/app/$application/",
                    params: {
                        organization,
                        project,
                        application: app.name
                    },
                    activeProps: {
                        className: 'text-white transition-all duration-200'
                    },
                    inactiveProps: {
                        className: 'text-gray-200/70'
                    },
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 font-light",
                        children: [
                            /*#__PURE__*/ _jsx(Package, {
                                className: "h-4 w-4"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "truncate group-hover/app-item:text-white",
                                children: /*#__PURE__*/ _jsx(HighlightText, {
                                    text: app.name,
                                    query: searchQuery
                                })
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(ApplicationContextMenu, {
                application: app.name,
                project: project,
                organization: organization,
                onOpenChange: setIsMenuOpen
            })
        ]
    });
}
function HighlightText(param) {
    let { text, query } = param;
    if (!query) return /*#__PURE__*/ _jsx(_Fragment, {
        children: text
    });
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return /*#__PURE__*/ _jsx(_Fragment, {
        children: text
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            text.slice(0, index),
            /*#__PURE__*/ _jsx("span", {
                className: "bg-muted rounded-[2px]",
                children: text.slice(index, index + query.length)
            }),
            text.slice(index + query.length)
        ]
    });
}
export function ProjectItem(param) {
    let { project, organization, isExpanded, onToggle, searchQuery = '', searchApplications } = param;
    var _ref;
    const matchRoute = useMatchRoute();
    const [isHovered, setIsHovered] = useState(false);
    const [isIconHovered, setIsIconHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const isProjectActive = !!matchRoute({
        to: "/org/$organization/$project",
        params: {
            organization,
            project: project.name
        },
        fuzzy: true
    }) && !location.pathname.includes('/app/');
    const isSearchActive = !!searchQuery;
    // When searchApplications is provided (search is active), skip the per-project fetch.
    const perProjectEnabled = !isSearchActive && (isExpanded || false);
    const { data: applicationListResponse, isLoading, refetch } = useGetProjectApplicationSidebarList({
        organization,
        project: project.name
    }, {
        query: {
            enabled: perProjectEnabled
        }
    });
    // During search use the pre-fetched apps from the single sidebar-search request.
    // Outside search, use the per-project lazy fetch.
    const applicationList = isSearchActive ? searchApplications !== null && searchApplications !== void 0 ? searchApplications : [] : (_ref = applicationListResponse === null || applicationListResponse === void 0 ? void 0 : applicationListResponse.entities) !== null && _ref !== void 0 ? _ref : [];
    const projectNameMatches = isSearchActive ? project.displayName.toLowerCase().includes(searchQuery.toLowerCase()) || project.name.toLowerCase().includes(searchQuery.toLowerCase()) : true;
    // Apps filtered by query — used to decide which apps to render when the project name itself doesn't match.
    const matchingApps = isSearchActive ? applicationList.filter((app)=>app.name.toLowerCase().includes(searchQuery.toLowerCase())) : applicationList;
    // During search: visibility is determined server-side (project only appears if name or apps match).
    // searchApplications is provided → data is ready, no loading guards needed.
    // During non-search: no filtering.
    // Which apps to render: when searching and project name doesn't match, show only matching apps.
    const appsToShow = isSearchActive && !projectNameMatches ? matchingApps : applicationList;
    // Whether to show the apps sub-list
    const shouldShowApps = isSearchActive ? appsToShow.length > 0 : isExpanded;
    // Chevron rotation follows whether apps are currently shown
    const isEffectivelyExpanded = isSearchActive ? shouldShowApps : isExpanded;
    const fetchIfNeeded = ()=>{
        if (!applicationListResponse && !isSearchActive) {
            refetch().catch(()=>{
                // eslint-disable-next-line no-console
                console.error('Failed to fetch application list on expand');
            });
        }
    };
    return /*#__PURE__*/ _jsxs(SidebarMenuItem, {
        "data-e2e": "LAYOUT_SIDEBAR__PROJECT_ITEM",
        "data-project-name": project.name,
        onMouseEnter: ()=>{
            setIsHovered(true);
            fetchIfNeeded();
        },
        onMouseLeave: ()=>setIsHovered(false),
        children: [
            /*#__PURE__*/ _jsx(SidebarMenuButton, {
                asChild: true,
                "data-active": isProjectActive,
                className: cn('font-normal cursor-pointer data-[active=true]:bg-transparent', isMenuOpen && 'bg-sidebar-accent'),
                "data-e2e": "LAYOUT_SIDEBAR__PROJECT_TOGGLE",
                children: /*#__PURE__*/ _jsxs(Link, {
                    to: "/org/$organization/$project",
                    params: {
                        organization,
                        project: project.name
                    },
                    children: [
                        applicationList.length || isSearchActive ? /*#__PURE__*/ _jsxs("span", {
                            className: cn('relative h-4 w-4 flex-shrink-0 cursor-pointer rounded-[4px] transition-colors', isIconHovered && 'bg-sidebar'),
                            onMouseEnter: ()=>setIsIconHovered(true),
                            onMouseLeave: ()=>setIsIconHovered(false),
                            onClick: (e)=>{
                                e.preventDefault();
                                e.stopPropagation();
                                onToggle();
                            },
                            children: [
                                isProjectActive ? /*#__PURE__*/ _jsx("span", {
                                    className: "absolute inset-0 transition-opacity text-white",
                                    children: /*#__PURE__*/ _jsx(Folder, {
                                        className: "h-4 w-4",
                                        fill: "white"
                                    })
                                }) : /*#__PURE__*/ _jsx("span", {
                                    className: "absolute inset-0 transition-opacity text-muted-foreground group-hover/menu-item:opacity-0",
                                    children: /*#__PURE__*/ _jsx(Folder, {
                                        className: "h-4 w-4"
                                    })
                                }),
                                !isProjectActive && /*#__PURE__*/ _jsx("span", {
                                    className: "absolute inset-0 transition-opacity text-muted-foreground opacity-0 group-hover/menu-item:opacity-100",
                                    children: /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: cn('h-4 w-4 transition-transform', isEffectivelyExpanded && 'rotate-90')
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx("span", {
                            className: "h-4 w-4 flex-shrink-0 text-muted-foreground",
                            children: /*#__PURE__*/ _jsx(Folder, {
                                className: "h-4 w-4",
                                fill: isProjectActive ? 'white' : 'none'
                            })
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: cn('truncate font-normal', (isHovered || isProjectActive) && 'text-white font-medium'),
                            children: /*#__PURE__*/ _jsx(HighlightText, {
                                text: project.displayName,
                                query: searchQuery
                            })
                        }),
                        /*#__PURE__*/ _jsx(ProjectContextMenu, {
                            project: project.name,
                            organization: organization,
                            onOpenChange: setIsMenuOpen
                        })
                    ]
                })
            }),
            shouldShowApps && /*#__PURE__*/ _jsx(SidebarMenuSub, {
                "data-e2e": "LAYOUT_SIDEBAR__APPLICATIONS_LIST",
                children: isLoading ? /*#__PURE__*/ _jsx(SidebarMenuSubItem, {
                    children: /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-6 w-full"
                    })
                }) : appsToShow === null || appsToShow === void 0 ? void 0 : appsToShow.sort((a, b)=>a.name.localeCompare(b.name)).map((app)=>{
                    return /*#__PURE__*/ _jsx(AppSubItem, {
                        app: app,
                        organization: organization,
                        project: project.name,
                        searchQuery: searchQuery
                    }, app.id);
                })
            })
        ]
    });
}
